/**
 * Building blocks of the game setup page (docs/setup-redesign/README.md, G1).
 *
 * Every setting is a SetupField: on desktop and tablet ("stacked") a small
 * label above the control, at one fixed height so rows line up across the
 * three columns; on phone, non-segmented settings are rows (name left,
 * control right). The "?" sits next to the label, and a changed setting shows
 * an amber dot there until it's saved. Dropdowns and steppers share one size.
 */
import React from 'react'
import { View, Text, TouchableOpacity, TextInput, Switch } from 'react-native'
import { HelpButton } from '../../ui/HelpButton'
import { InfoModal } from '../../ui/InfoModal'

export type SetupLayout = 'stacked' | 'row'

/** One size for every dropdown and stepper, per layout. */
export const SETUP_CONTROL = {
  stacked: { width: 140, height: 38 },
  row: { width: 120, height: 36 },
} as const
/** Stacked items: label line + control box, so rows align across columns. */
export const STACKED_LABEL_HEIGHT = 18
export const STACKED_CONTROL_HEIGHT = 40
export const SETUP_AMBER = '#b45309'
const CHANGED_DOT = '#d97706'

export interface SetupHelp {
  label: string
  title: string
  message: string | readonly string[]
  testID?: string
}

export function ChangedDot() {
  return <View accessibilityLabel="Changed, not saved yet" testID="setup-changed-dot"
    style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: CHANGED_DOT, marginLeft: 6 }} />
}

function HelpBox({ help, onClose, visible }: { help: SetupHelp; visible: boolean; onClose: () => void }) {
  return typeof help.message === 'string'
    ? <InfoModal visible={visible} variant="help" onClose={onClose} title={help.title} message={help.message} />
    : <InfoModal visible={visible} variant="help" onClose={onClose} title={help.title}>
      {help.message.map((paragraph, i) => <Text key={i} className="font-typed text-ink text-sm">{paragraph}</Text>)}
    </InfoModal>
}

/**
 * A labelled setting. `stacked` puts the label (" · hint") above the control
 * at a fixed height; `row` puts the name and hint on the left and the control
 * on the right. `note` is a line under the setting (e.g. why it's locked).
 */
export function SetupField({ label, hint, help, changed, layout, separated, note, noteTone = 'muted', noteUnderControl, children }: {
  label: string
  hint?: string
  help?: SetupHelp
  changed?: boolean
  layout: SetupLayout
  /** Row layout: dashed rule above, between consecutive rows. */
  separated?: boolean
  note?: string | null
  noteTone?: 'muted' | 'warn'
  /** Row layout: put the note under the control (right-aligned) rather than full width. */
  noteUnderControl?: boolean
  children: React.ReactNode
}) {
  const [helpOpen, setHelpOpen] = React.useState(false)
  const helpButton = help && <HelpButton size={20} testID={help.testID} accessibilityLabel={help.label} onPress={() => setHelpOpen(true)} />
  const helpBox = help && <HelpBox help={help} visible={helpOpen} onClose={() => setHelpOpen(false)} />
  const noteLine = note ? <Text className={`font-typed text-xs leading-4 ${noteTone === 'warn' ? 'text-blood' : 'text-ink-soft'}`}
    style={{ marginTop: layout === 'stacked' ? -6 : 0, marginBottom: layout === 'stacked' ? 12 : 8,
      ...(layout === 'row' && noteUnderControl ? { textAlign: 'right' as const, alignSelf: 'flex-end' as const } : null) }}>{note}</Text> : null
  if (layout === 'row') {
    return <>
      <View className={`flex-row items-center justify-between ${separated ? 'border-t border-dashed border-olive/40' : ''}`}
        style={{ gap: 12, paddingVertical: 12 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <View className="flex-row items-center" style={{ gap: 6 }}>
            <Text className="font-typed text-ink" style={{ fontSize: 15 }}>{label}</Text>
            {changed && <ChangedDot />}
            {helpButton}
          </View>
          {hint ? <Text className="font-typed text-ink-soft text-xs mt-0.5">{hint}</Text> : null}
        </View>
        {children}
      </View>
      {noteLine}
      {helpBox}
    </>
  }
  return <>
    <View style={{ marginBottom: 14, gap: 8 }}>
      <View className="flex-row items-center" style={{ height: STACKED_LABEL_HEIGHT, gap: 6 }}>
        <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up" numberOfLines={1} style={{ flexShrink: 1 }}>
          {hint ? `${label} · ${hint}` : label}
        </Text>
        {changed && <ChangedDot />}
        {helpButton}
      </View>
      <View style={{ minHeight: STACKED_CONTROL_HEIGHT, justifyContent: 'center', alignItems: 'flex-start' }}>{children}</View>
    </View>
    {noteLine}
    {helpBox}
  </>
}

/**
 * −/value/+ with a typed value. Holds its own text so the field can be cleared
 * and retyped; the parent only gets a number once it parses within [min, max],
 * and `onInvalid` reports an empty or out-of-range field so Save can be gated.
 */
export function SetupStepper({ value, min, max, onChange, onInvalid, name, layout }: {
  value: number
  min: number
  max: number
  onChange: (v: number) => void
  onInvalid?: (bad: boolean) => void
  /** Accessible name: "Decrease <name>", "Increase <name>". */
  name: string
  layout: SetupLayout
}) {
  const [text, setText] = React.useState(String(value))
  // Sync down when the parent changes the value (±, a map snapping the seat
  // count), without fighting someone mid-typing the same number.
  React.useEffect(() => {
    setText((prev) => (parseInt(prev, 10) === value ? prev : String(value)))
  }, [value])
  const parsed = parseInt(text, 10)
  const valid = !Number.isNaN(parsed) && parsed >= min && parsed <= max
  React.useEffect(() => {
    onInvalid?.(!valid)
  }, [valid, onInvalid])
  // On unmount, clear any error this stepper set.
  React.useEffect(() => () => onInvalid?.(false), [onInvalid])

  const set = (next: number) => { onChange(next); setText(String(next)) }
  // ± work from the last good value while the field is mid-edit.
  const base = valid ? parsed : value
  const size = SETUP_CONTROL[layout]
  const atMin = base <= min
  const atMax = base >= max
  const button = (glyph: string, label: string, disabled: boolean, onPress: () => void, side: 'l' | 'r') =>
    <TouchableOpacity onPress={onPress} disabled={disabled} accessibilityLabel={label} accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={`items-center justify-center bg-paper ${side === 'l' ? 'border-r' : 'border-l'} border-olive`}
      style={{ width: 38, height: '100%', opacity: disabled ? 0.35 : 1 }}>
      <Text className="font-stencil text-ink text-lg">{glyph}</Text>
    </TouchableOpacity>
  return <View>
    <View className={`flex-row items-center bg-paper-up border-2 rounded-md ${valid ? 'border-olive' : 'border-blood'}`}
      style={{ ...size, overflow: 'hidden' }}>
      {button('−', `Decrease ${name}`, atMin, () => set(Math.max(min, base - 1)), 'l')}
      <TextInput value={text} keyboardType="number-pad" accessibilityLabel={name} maxLength={3} selectTextOnFocus
        onChangeText={(t) => {
          const clean = t.replace(/[^0-9]/g, '')
          setText(clean)
          const n = parseInt(clean, 10)
          if (!Number.isNaN(n) && n >= min && n <= max) onChange(n)
        }}
        className="flex-1 text-center font-stencil text-base text-ink" style={{ minWidth: 0, paddingVertical: 0, height: '100%' }} />
      {button('+', `Increase ${name}`, atMax, () => set(Math.min(max, base + 1)), 'r')}
    </View>
    {!valid && <Text className="font-typed text-[10px] text-blood italic mt-1">{min}–{max}</Text>}
  </View>
}

export function SetupSwitch({ value, onChange, name, disabled }: { value: boolean; onChange: (v: boolean) => void; name: string; disabled?: boolean }) {
  return <Switch value={value} onValueChange={onChange} accessibilityLabel={`${name} ${value ? 'on' : 'off'}`} disabled={disabled}
    accessibilityState={disabled ? { disabled: true, checked: value } : undefined}
    trackColor={{ false: '#475569', true: '#B91C1C' }} ios_backgroundColor="#475569" thumbColor="#f5f5f5" />
}

/** A setting the map's special rules decide: read-only, in a dashed amber box. */
export function LockedValue({ value }: { value: string }) {
  return <View accessibilityLabel={`Set by the map: ${value}`} className="rounded-md justify-center"
    style={{ minHeight: SETUP_CONTROL.stacked.height, paddingHorizontal: 12, paddingVertical: 6, alignSelf: 'stretch',
      borderWidth: 2, borderStyle: 'dashed', borderColor: SETUP_AMBER, backgroundColor: 'rgba(180,83,9,0.08)' }}>
    <Text className="font-stencil text-ink text-xs" style={{ letterSpacing: 0.5 }}>★ {value}</Text>
  </View>
}
